import { setBulkProcessUpdate, setImportBatch, setImportRows } from "../slice/ingestSlice";
import { setNotification } from "../slice/miscSlice";
import { apiSlice } from "./apiSlice";
import { INVALID_ROW_TOAST, nextInvalidRowToastState } from "./ingestInvalidRowToast";
import { fileResponseHandler } from "./utils";

let invalidRowToastShown = { batchId: null, invalidRows: null };

export const ingestApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    commitBatch: builder.mutation({
      query: ({ importBatchId }) => ({
        url: `/api_ingest/bulk/batches/${importBatchId}/commit/`,
        method: "POST",
      }),
    }),
    downloadBulkFormatCreate: builder.query({
      async queryFn(arg, _api, _extra, baseQuery) {
        try {
          const res = await baseQuery({
            url: `/api_ingest/bulk/${arg.module}/template/`,
            responseHandler: fileResponseHandler,
          });

          if ("error" in res) {
            const detail = res.error?.data?.detail;
            return {
              error: {
                status: res.error?.status,
                message:
                  detail || "Failed to download format. Something went wrong.",
              },
            };
          }

          const blob = res.data;
          if (!(blob instanceof Blob)) {
            return { error: { status: 500, message: "Invalid file response" } };
          }

          // trigger download
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = arg.fileName;
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);

          return { data: undefined };
        } catch (err) {
          // catch any runtime crash here
          return { error: { status: 500, message: "Something went wrong. Please try again." } };
        }
      },
      keepUnusedDataFor: 0,
    }),
    downloadBulkFormatUpdate: builder.query({
      async queryFn(arg, _api, _extra, baseQuery) {
        try {
          const res = await baseQuery({
            url: `/api_ingest/bulk/${arg.module}/template/update/`,
            method: "POST",
            body: { ids: arg.ids },
            responseHandler: fileResponseHandler,
          });

          if ("error" in res) {
            const detail = res.error?.data?.detail;
            return {
              error: {
                status: res.error?.status,
                message:
                  detail || "Failed to download format. Something went wrong.",
              },
            };
          }

          const blob = res.data;
          if (!(blob instanceof Blob)) {
            return { error: { status: 500, message: "Invalid file response" } };
          }

          // trigger download
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = arg.fileName;
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);

          return { data: undefined };
        } catch (err) {
          // catch any runtime crash here
          return { error: { status: 500, message: "Something went wrong. Please try again." } };
        }
      },
      keepUnusedDataFor: 0,
    }),
    getImportBatch: builder.query({
      query: importBatchId => `/api_ingest/bulk/batches/${importBatchId}/status/`,
      async onQueryStarted(importBatchId, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setImportBatch(data));
          const toastState = nextInvalidRowToastState(
            {
              batchId: data?.id || importBatchId,
              invalidRows: data?.invalid_rows,
            },
            invalidRowToastShown,
          );
          invalidRowToastShown = toastState.lastShown;
          if (toastState.show) {
            dispatch(setNotification(INVALID_ROW_TOAST));
          }
        } catch {}
      },
    }),
    getImportRows: builder.query({
      query: ({ importBatchId, params }) => ({
        url: `/api_ingest/bulk/batches/${importBatchId}/preview/`,
        params: params,
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setImportRows(data));
        } catch {}
      },
    }),
    downloadBulkErrors: builder.query({
      async queryFn(arg, _api, _extra, baseQuery) {
        try {
          const res = await baseQuery({
            url: `/api_ingest/bulk/batches/${arg.importBatchId}/errors/export/`,
            responseHandler: fileResponseHandler,
          });

          if ("error" in res) {
            return {
              error: {
                status: res.error?.status,
                message: "Failed to export errors. Something went wrong.",
              },
            };
          }

          const blob = res.data;
          if (!(blob instanceof Blob)) {
            return { error: { status: 500, message: "Invalid file response" } };
          }

          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = arg.fileName;
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);

          return { data: undefined };
        } catch (err) {
          return { error: { status: 500, message: "Something went wrong. Please try again." } };
        }
      },
      keepUnusedDataFor: 0,
    }),
    uploadBulkFormatCreate: builder.mutation({
      query: ({ module, formData }) => ({
        url: `/api_ingest/bulk/${module}/upload/`,
        method: "POST",
        body: formData,
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setImportBatch(data));
        } catch {}
      },
    }),
    getImportEvents: builder.query({
      query: importBatchId => `/api_ingest/bulk/batches/${importBatchId}/events/`,
      async onQueryStarted(importBatchId, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          // Replay each event into the store so the UI catches up on any
          // progress/stage events that were published before the SSE
          // subscription was established (Redis pub/sub has no history).
          if (Array.isArray(data)) {
            for (const event of data) {
              dispatch(setBulkProcessUpdate({ import_event: event }));
            }
          }
          if (importBatchId) {
            dispatch(
              ingestApiSlice.endpoints.getImportBatch.initiate(importBatchId, {
                forceRefetch: true,
              }),
            );
          }
        } catch {}
      },
      keepUnusedDataFor: 0,
    }),
  }),
});

export const {
  useCommitBatchMutation,
  useGetImportBatchQuery,
  useGetImportRowsQuery,
  useLazyDownloadBulkErrorsQuery,
  useLazyDownloadBulkFormatCreateQuery,
  useLazyDownloadBulkFormatUpdateQuery,
  useLazyGetImportEventsQuery,
  useUploadBulkFormatCreateMutation,
} = ingestApiSlice;
